import React, { createContext, useContext, useState, ReactNode } from "react";

interface WarrantyFormData {
    [key: string]: any;
}

interface PopupFormData {
    [key: string]: any;
}

interface WarrantyModalState {
    active: boolean;
    content: string | undefined;
}

interface WarrantyState {
    warrantyForm: WarrantyFormData;
    popupForm: PopupFormData;
    modal: WarrantyModalState;
    fields: any;
}

interface WarrantyContextType {
    warrantyState: WarrantyState;
    setWarrantyForm: (data: Partial<WarrantyFormData>) => void;
    setPopupForm: (data: Partial<PopupFormData>) => void;
    setWarrantyModal: (modal: WarrantyModalState) => void;
    setFields: (fields: any) => void;
    closeWarrantyModal: () => void;
}

const WarrantyContext = createContext<WarrantyContextType | undefined>(
    undefined,
);

export const WarrantyProvider = ({ children }: { children: ReactNode }) => {
    const [warrantyState, setWarrantyState] = useState<WarrantyState>({
        warrantyForm: {},
        popupForm: {},
        modal: { active: false, content: undefined },
        fields: {},
    });

    const setWarrantyForm = (data: Partial<WarrantyFormData>) => {
        setWarrantyState((prev) => ({
            ...prev,
            warrantyForm: { ...prev.warrantyForm, ...data },
        }));
    };

    const setPopupForm = (data: Partial<PopupFormData>) => {
        setWarrantyState((prev) => ({
            ...prev,
            popupForm: { ...prev.popupForm, ...data },
        }));
    };

    const setWarrantyModal = (modal: WarrantyModalState) => {
        setWarrantyState((prev) => ({ ...prev, modal }));
    };

    const setFields = (fields: Record<string, any>) => {
        setWarrantyState((prev) => ({ ...prev, fields }));
    };

    const closeWarrantyModal = () => {
        setWarrantyState((prev) => ({
            ...prev,
            modal: { active: false, content: undefined },
        }));
    };

    return (
        <WarrantyContext.Provider
            value={{
                warrantyState,
                setWarrantyForm,
                setPopupForm,
                setWarrantyModal,
                setFields,
                closeWarrantyModal,
            }}
        >
            {children}
        </WarrantyContext.Provider>
    );
};

export const useWarranty = () => {
    const context = useContext(WarrantyContext);
    if (context === undefined) {
        throw new Error("useWarranty must be used within a WarrantyProvider");
    }
    return context;
};
